// App.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "./App.css";
import Modal from "react-modal";
import { getDifficultyLevelText, getTimeComplexityLevelText } from "./utils";

Modal.setAppElement("#root");
// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const API_URL = process.env.REACT_APP_API_URL;

function App() {
  const [mainReviewerName, setMainReviewerName] = useState("");
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [reviewFilter, setReviewFilter] = useState("all"); // 'all', 'good', 'bad', 'pending'
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("good");
  const [reviewReason, setReviewReason] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [pageValue, setPageValue] = useState("1");
  const [isEditable, setIsEditable] = useState(false);
  const [isPdfViewOpen, setIsPdfViewOpen] = useState(false);

  useEffect(() => {
    if (!questions.length) {
      setFilteredQuestions([]);
      return;
    }

    if (reviewFilter === "all") {
      setFilteredQuestions(questions);
    } else {
      setFilteredQuestions(
        questions.filter((q) => q.review_status === reviewFilter)
      );
    }
  }, [questions, reviewFilter]);

  // Fetch books on component mount
  useEffect(() => {
    fetchBooks();
  }, []);

  // Fetch questions when a book is selected
  useEffect(() => {
    if (selectedBook) {
      fetchQuestions(selectedBook.book_code);
    }
  }, [selectedBook]);

  // Update page number when a question is selected
  useEffect(() => {
    if (selectedQuestion && selectedQuestion.book_page_number) {
      setPageNumber(selectedQuestion.book_page_number);
    }
  }, [selectedQuestion]);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/books`);
      setBooks(response.data);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  const fetchQuestions = async (bookCode) => {
    try {
      const response = await axios.get(`${API_URL}/questions/${bookCode}`);
      setQuestions(response.data);
      setFilteredQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const fetchQuestion = async (uid) => {
    try {
      const response = await axios.get(`${API_URL}/question/${uid}`);
      setSelectedQuestion(response.data);

      // Also fetch the review to get reviewer name
      const reviewResponse = await axios.get(`${API_URL}/reviews/${uid}`);
      if (reviewResponse.data) {
        setSelectedQuestion((prev) => ({
          ...prev,
          review_status: reviewResponse.data.status || "pending",
          review_reason: reviewResponse.data.reason,
          reviewer_name: reviewResponse.data.reviewer_name,
        }));
      }
    } catch (error) {
      console.error("Error fetching question details:", error);
    }
  };

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setSelectedQuestion(null);
    setPdfFile(null);
  };

  const handleQuestionSelect = (question) => {
    fetchQuestion(question.uid);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setPdfFile(file);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "good":
        return "✅";
      case "bad":
        return "❌";
      default:
        return "⏳";
    }
  };

  // Add these functions to handle review submission
  const openReviewModal = () => {
    if (!selectedQuestion) return;

    setReviewStatus(
      selectedQuestion.review_status === "pending"
        ? "good"
        : selectedQuestion.review_status
    );
    setReviewReason(selectedQuestion.review_reason || "");
    setReviewerName(selectedQuestion.reviewer_name || "");
    setIsReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
  };

  const submitReview = async () => {
    if (!selectedQuestion) return;

    // Use main reviewer name if available, otherwise use the modal's name
    const finalReviewerName = mainReviewerName.trim() || reviewerName.trim();

    if (!finalReviewerName) {
      alert("Please enter your name before submitting the review.");
      return;
    }

    try {
      await axios.post(`${API_URL}/reviews`, {
        question_uid: selectedQuestion.uid,
        status: reviewStatus,
        reason: reviewStatus === "bad" ? reviewReason : null,
        reviewer_name: finalReviewerName,
      });

      // Update the local state
      const updatedQuestions = questions?.map((q) => {
        if (q.uid === selectedQuestion.uid) {
          return {
            ...q,
            review_status: reviewStatus,
            review_reason: reviewStatus === "bad" ? reviewReason : null,
            reviewer_name: finalReviewerName,
          };
        }
        return q;
      });

      setQuestions(updatedQuestions);
      setSelectedQuestion({
        ...selectedQuestion,
        review_status: reviewStatus,
        review_reason: reviewStatus === "bad" ? reviewReason : null,
        reviewer_name: finalReviewerName,
      });

      closeReviewModal();
    } catch (error) {
      console.error("Error submitting review:", error);
    }
  };

  // Parse options from JSON string
  // Parse options from string array
  const renderOptions = (optionsString) => {
    try {
      // Check if optionsString is already an array
      let cleanedOptionString = optionsString.replace(/'/g, '"');
      const options =
        typeof cleanedOptionString === "string" &&
        !Array.isArray(cleanedOptionString)
          ? JSON.parse(cleanedOptionString)
          : cleanedOptionString;

      return (
        <div className="options">
          {Array.isArray(options) &&
            options?.map((option, index) => (
              <div
                key={index}
                className={`option ${
                  selectedQuestion && selectedQuestion.answer === index
                    ? "correct-answer"
                    : ""
                }`}
              >
                {index + 1}. {option}
              </div>
            ))}
        </div>
      );
    } catch (e) {
      // If parsing fails, try to display as a simple string
      console.error("Error parsing options:", e);
      return (
        <div className="options">
          <div>Options format error: {optionsString}</div>
        </div>
      );
    }
  };

  const renderPdfView = (zoom = 1) => {
    return (
      <div className="column col-span-4">
        {pdfFile ? (
          <div>
            <div className="pdf-controls">
              <button
                onClick={() =>
                  setPageNumber(pageNumber > 1 ? pageNumber - 1 : 1)
                }
                disabled={pageNumber <= 1}
              >
                Previous
              </button>
              <span>
                Page {renderPageEditContent()} of {numPages}
              </span>
              <button
                onClick={() =>
                  setPageNumber(
                    pageNumber < numPages ? pageNumber + 1 : numPages
                  )
                }
                disabled={pageNumber >= numPages}
              >
                Next
              </button>
            </div>
            <div>
              <Document
                className="pdf-view"
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page scale={zoom} pageNumber={pageNumber} />
              </Document>
              {!isPdfViewOpen && (
                <button
                  onClick={() => setIsPdfViewOpen(true)}
                  style={{ position: "fixed", bottom: 30, right: 50 }}
                >
                  Zoom In
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="pdf-placeholder">
            <p>Upload a PDF to view the book content</p>
          </div>
        )}
      </div>
    );
  };

  const renderPageEditContent = () => {
    if (isEditable) {
      return (
        <input
          className="max-w-[30px] border-0 text-center text-base overflow-visible"
          value={pageValue}
          autoFocus
          onChange={(e) => setPageValue(e?.target?.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const newPage = parseInt(pageValue, 10);
              if (newPage >= 1 && newPage <= numPages) {
                setPageNumber(newPage);
                setIsEditable(false);
              } else {
                // Reset input value if the page number is invalid
                setPageValue(pageNumber);
                setIsEditable(false);
              }
            }
          }}
          onBlur={() => {
            setIsEditable(false);
          }}
        />
      );
    }

    return (
      <span
        onClick={() => {
          setPageValue(pageNumber);
          setIsEditable(true);
        }}
      >
        {pageNumber}
      </span>
    );
  };

  return (
    <div className="app">
      <header>
        <h1>Book Question Review System</h1>
        <div className="reviewer-name-main">
          <label htmlFor="main-reviewer-name">Your Name:</label>
          <input
            type="text"
            id="main-reviewer-name"
            value={mainReviewerName}
            onChange={(e) => setMainReviewerName(e.target.value)}
            placeholder="Enter your name for reviews"
          />
        </div>
        <div className="book-selector">
          <select
            onChange={(e) => {
              const book = books.find((b) => b.book_code === e.target.value);
              if (book) handleBookSelect(book);
            }}
          >
            <option value="">Select a book</option>
            {books?.map((book) => (
              <option key={book.book_code} value={book.book_code}>
                {book.book_name}
              </option>
            ))}
          </select>
          {selectedBook && (
            <div>
              <label htmlFor="pdf-upload">Upload PDF: </label>
              <input
                type="file"
                id="pdf-upload"
                accept="application/pdf"
                onChange={handleFileUpload}
              />
            </div>
          )}
        </div>
      </header>

      <div className="grid grid-cols-10 flex-1 overflow-hidden">
        {/* Left Column - Question List */}
        <div className="column question-list col-span-2">
          <div className="filter-controls">
            <label>Filter by review status:</label>
            <select
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
            >
              <option value="all">All Questions</option>
              <option value="good">Approved ✅</option>
              <option value="bad">Rejected ❌</option>
              <option value="pending">Pending ⏳</option>
            </select>
          </div>
          <h2>Questions</h2>
          {filteredQuestions?.length > 0 ? (
            <ul>
              {filteredQuestions?.map((question, i) => (
                <li
                  key={i}
                  onClick={() => handleQuestionSelect(question)}
                  className={
                    selectedQuestion && selectedQuestion.uid === question.uid
                      ? "selected"
                      : ""
                  }
                >
                  <span className="status-icon">
                    {getStatusIcon(question.review_status)}
                  </span>
                  {question.uid}
                </li>
              ))}
            </ul>
          ) : (
            <p>No questions available for this filter</p>
          )}
        </div>

        {/* Middle Column - Question Details */}
        <div className="column col-span-4 overflow-y-auto">
          {selectedQuestion ? (
            <div>
              <h2>Question: {selectedQuestion.uid}</h2>
              <div className="question-type">
                Type: {selectedQuestion.question_type} (
                {selectedQuestion.question_acro})
              </div>

              <p
                className="question-text"
                dangerouslySetInnerHTML={{
                  __html: selectedQuestion?.question?.replace(/\\n/g, "<br />"),
                }}
              />

              {selectedQuestion.options &&
                renderOptions(selectedQuestion.options)}
              <div className="question-metadata">
                <p>
                  Difficulty: {getDifficultyLevelText(selectedQuestion.diff)}
                </p>
                <p>
                  Time Complexity Level:{" "}
                  {getTimeComplexityLevelText(selectedQuestion.tc)}
                </p>
                <p>Page: {selectedQuestion.book_page_number}</p>
              </div>
              {selectedQuestion.reasoning && (
                <div className="reasoning">
                  <h3>AI's Reasoning:</h3>
                  <p>{selectedQuestion.reasoning}</p>
                </div>
              )}
              {selectedQuestion && (
                <div className="review-section">
                  <h3>
                    Review Status:{" "}
                    {getStatusIcon(selectedQuestion.review_status)}{" "}
                    {selectedQuestion.review_status}
                  </h3>
                  {selectedQuestion.review_status !== "pending" && (
                    <div className="reviewer-info">
                      <p>
                        <strong>Reviewed by:</strong>{" "}
                        {selectedQuestion.reviewer_name || "Unknown"}
                      </p>
                    </div>
                  )}
                  {selectedQuestion.review_status === "bad" &&
                    selectedQuestion.review_reason && (
                      <div className="review-reason">
                        <p>
                          <strong>Reason:</strong>{" "}
                          {selectedQuestion.review_reason}
                        </p>
                      </div>
                    )}
                  <button className="review-button" onClick={openReviewModal}>
                    {selectedQuestion.review_status === "pending"
                      ? "Add Review"
                      : "Update Review"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p>Select a question to view details</p>
          )}
        </div>

        {/* Right Column - PDF Viewer */}
        {renderPdfView()}
      </div>
      <Modal
        isOpen={isReviewModalOpen}
        onRequestClose={closeReviewModal}
        contentLabel="Review Question"
        className="review-modal"
        overlayClassName="review-modal-overlay"
      >
        <h2>Review Question: {selectedQuestion?.uid}</h2>

        <div className="review-form">
          {/* Only show name input if no name in main UI */}
          {!mainReviewerName.trim() && (
            <div className="reviewer-name-input">
              <label htmlFor="reviewer_name">Your Name:</label>
              <input
                type="text"
                id="reviewer-name"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="Enter your name"
                required
              />
            </div>
          )}

          <div className="review-status-selector">
            <label>
              <input
                type="radio"
                name="reviewStatus"
                value="good"
                checked={reviewStatus === "good"}
                onChange={() => setReviewStatus("good")}
              />
              Approve ✅
            </label>

            <label>
              <input
                type="radio"
                name="reviewStatus"
                value="bad"
                checked={reviewStatus === "bad"}
                onChange={() => setReviewStatus("bad")}
              />
              Reject ❌
            </label>
          </div>

          {reviewStatus === "bad" && (
            <div className="review-reason-input">
              <label htmlFor="review-reason">Reason for rejection:</label>
              <textarea
                id="review-reason"
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder="Explain why this question is being rejected..."
                rows={4}
              />
            </div>
          )}

          <div className="review-modal-buttons">
            <button onClick={submitReview} className="submit-review">
              Submit Review
            </button>
            <button onClick={closeReviewModal} className="cancel-review">
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isPdfViewOpen}
        onRequestClose={() => setIsPdfViewOpen(false)}
      >
        {renderPdfView(2.5)}
      </Modal>
    </div>
  );
}

export default App;
